/**
 * Helpers para agrupar Conjuntos/Grupos Homogéneos (G.H.) por familia de
 * molécula y detectar G.H. atípicos.
 *
 * La unidad principal es el G.H. (mismo principio activo + dosis + forma).
 * Varios G.H. de la misma familia molecular (p.ej. ALPRAZOLAM 0,5 mg /
 * 1 mg / 2 mg) se agrupan visualmente bajo el nombre del principio activo
 * para poder comparar entre sí. Dentro de ese grupo, se marca como ATÍPICO
 * el G.H. cuyo % genérico está significativamente POR DEBAJO de la mediana
 * de los OTROS G.H. de la familia (sólo desviación negativa).
 */

const STOP_TOKENS = new Set([
  "MG", "G", "ML", "MCG", "UI", "EFG", "COMP", "CPR", "CAPS",
  "CAPSULAS", "COMPRIMIDOS", "RECUBIERTOS", "BUCODISPERSABLES", "SOBRES",
  "GRAGEAS", "GOTAS", "JARABE", "SOLUCION", "INYECTABLE", "AMPOLLAS", "EFERVESCENTES",
  "ENTERICOS", "GASTRORRESISTENTES", "GASTRO", "DISPERSABLES", "DISPERSABLE",
  "RANURADOS", "MASTICABLES", "RETARD", "PROLONGADA", "RELEASE", "LIB",
]);

/**
 * Extrae el "principio activo" del nombre del G.H.
 *
 * Tomamos los tokens NO-numéricos del principio del nombre hasta el primer
 * token que comience con un dígito (dosis: 100, 70MG, 0,5%, etc.). Así
 * preservamos compuestos como "ACIDO ACETILSALICILICO" y distinguimos
 * "ACIDO ALENDRONICO" de "ACIDO ALENDRONICO/COLECALCIFEROL".
 *
 * Importante: el separador NO incluye "/" ni "." para no romper compuestos
 * (ALENDRONICO/COLECALCIFEROL, AMOXICILINA/ACIDO CLAVULANICO, etc.).
 */
export function moleculeRoot(name) {
  if (!name) return "";
  const cleaned = String(name).toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // Solo dividimos por espacios, comas, guiones y "+". Mantenemos "/" y "."
  // dentro del token para no perder principios activos compuestos.
  const tokens = cleaned.split(/[\s,\-+]+/).filter(Boolean);
  const significant = [];
  for (const t of tokens) {
    if (/^\d/.test(t)) break;            // primer número: empieza la dosis → parar
    if (STOP_TOKENS.has(t)) continue;    // descartar términos farmacéuticos
    if (t.length < 2) continue;
    significant.push(t);
  }
  if (significant.length === 0) return tokens[0] || "";
  return significant.join(" ");
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Agrupa G.H. (o CN) consecutivos por familia de molécula (moleculeRoot del
 * nombre/G.H.) y marca como ATÍPICOS aquellos cuyo % está significativamente
 * POR DEBAJO de la mediana de los OTROS items del mismo grupo (desviación
 * negativa). Items por encima NO se marcan.
 *
 * @param {Array} sorted - lista ya ordenada alfabéticamente
 * @param {Function} nameFn - extrae el nombre/G.H. para calcular la raíz (default: i => i.gh)
 * @param {Function} pctFn - extrae el % a comparar (p.ej. generic_share_pct)
 * @param {number} atypicalThreshold - p.p. de caída mínima vs la mediana del resto (default: 20)
 * @returns {Array<{root, items, isGroup, avgPct, medianPct}>}
 */
export function buildMoleculeGroups(
  sorted,
  nameFn = (i) => i.gh,
  pctFn = (i) => i.generic_share_pct,
  atypicalThreshold = 20,
) {
  const out = [];
  let cur = null;
  for (const m of sorted) {
    const r = moleculeRoot(nameFn(m));
    if (!cur || cur.root !== r) {
      cur = { root: r, items: [], isGroup: false, avgPct: null, medianPct: null };
      out.push(cur);
    }
    cur.items.push(m);
  }
  for (const g of out) {
    const valid = g.items.filter((m) => typeof pctFn(m) === "number");
    const vals = valid.map(pctFn);
    g.avgPct = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    g.medianPct = vals.length ? median(vals) : null;
    g.isGroup = g.items.length >= 2;
    g.items.forEach((m) => {
      m._isAtypical = false;
      if (!g.isGroup) return;
      const v = pctFn(m);
      if (typeof v !== "number") return;
      const others = valid.filter((x) => x !== m).map(pctFn);
      if (!others.length) return;
      const mOthers = median(others);
      if (mOthers == null) return;
      // Sólo desviación NEGATIVA (significativamente por debajo del resto)
      if (v <= mOthers - atypicalThreshold) m._isAtypical = true;
    });
  }
  return out;
}
